import { Link } from "react-router-dom";
import { LanguageMenu, SmartLink } from "@/components/Header";
import { footerColumns, footerExtraColumns, legalLinks } from "@/data/site";
import { EmailLink } from "@/components/EmailLink";
import { openCookieSettings } from "@/lib/consent";
import { NewsBox } from "@/components/NewsBox";

// A pale blue panel on plain ink (no glow or grid above it, owner 2026-09-30), and the
// ClearMakes name set very large underneath. Older pages wrap the footer in `.legacy`, which remaps classes such
// as text-white and bg-black, so this file uses explicit colours only.
const heading = "text-[12.5px] font-bold uppercase tracking-[0.12em] text-foreground";
const link = "text-[15px] text-[hsl(216_12%_32%)] transition-colors hover:text-foreground";

const Column = ({ heading: h, links }: { heading: string; links: { label: string; href: string }[] }) => (
  <nav aria-label={h}>
    <p className={heading}>{h}</p>
    <ul className="mt-5 space-y-3">
      {links.map((l) => (
        <li key={l.href + l.label}>
          <SmartLink href={l.href} className={link}>
            {l.label}
          </SmartLink>
        </li>
      ))}
    </ul>
  </nav>
);

export const Footer = () => {
  return (
    <footer className="relative overflow-hidden bg-[hsl(0_0%_4%)]">
      <div className="relative mx-auto max-w-[1320px] px-2 pt-8 sm:px-6 sm:pt-12">
        <div className="rounded-[28px] bg-[hsl(216_50%_96%)] px-6 pb-8 pt-12 sm:rounded-[36px] sm:px-12 sm:pt-16">
          <NewsBox />
          <div className="grid gap-x-8 gap-y-12 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
            {footerColumns.map((col) => (
              <Column key={col.heading} heading={col.heading} links={col.links} />
            ))}
            <div className="sm:col-span-2 md:col-span-1">
              <Link to="/" aria-label="ClearMakes home" className="inline-flex items-center gap-2.5">
                <img src="/logo-mark-256.png" alt="ClearMakes logo" width={36} height={36} className="h-9 w-9" />
                <span className="text-[1.35rem] font-bold tracking-[-0.03em] text-foreground">ClearMakes</span>
              </Link>
              <p className="mt-4 max-w-[260px] text-[15px] leading-snug text-[hsl(216_12%_32%)]">
                Less coordination. More finished work.
              </p>
              <EmailLink email="contact@clearmakes.com" className="mt-4 inline-block text-[15px] font-medium text-foreground cm-link">
                contact@clearmakes.com
              </EmailLink>
            </div>
          </div>

          <div className="mt-14 grid gap-x-8 gap-y-12 border-t border-[hsl(216_30%_86%)] pt-12 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
            {footerExtraColumns.map((col) => (
              <Column key={col.heading} heading={col.heading} links={col.links} />
            ))}
            <div className="hidden xl:col-span-2 xl:block" />
            <Column heading="Legal" links={legalLinks} />
          </div>

          <div className="mt-16 flex flex-col gap-5 border-t border-[hsl(216_30%_86%)] pt-7 text-[13.5px] text-[hsl(216_12%_38%)] sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <p>© {new Date().getFullYear()} ClearMakes. All rights reserved.</p>
              <button type="button" onClick={openCookieSettings} className="transition-colors hover:text-foreground">
                Cookie settings
              </button>
            </div>
            <LanguageMenu pill />
          </div>
        </div>
      </div>
      {/* the name, very large, fading into the page's end */}
      <p
        aria-hidden
        className="pointer-events-none relative -mb-[2.6vw] mt-6 select-none whitespace-nowrap text-center text-[16.5vw] font-bold leading-[0.9] tracking-[-0.06em]"
        style={{
          backgroundImage: "linear-gradient(180deg, hsl(216 84% 62% / 0.55), hsl(216 84% 52% / 0.08) 85%)",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          color: "transparent",
        }}
      >
        ClearMakes
      </p>
    </footer>
  );
};
